/**
 * Native (Android) push notifications: register FCM token with API and handle "replay ready" taps.
 * When the user is on the native app and goes offline/background, they still get a push when replay is ready.
 */
import { onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { registerDeviceToken, fetchRemoteFlight } from '@/services/sync';
import { isAuthenticated } from '@/services/api';
import { emitReplayReady } from '@/composables/useReplayReadyEvents';
import { setGenerating } from '@/composables/useReplayGenerating';
import { showReplayReadyAlert } from '@/utils/replayReadyAlert';
import { useFlightStorage } from '@/composables/useFlightStorage';

type PushData = {
  type?: string;
  flight_id?: string;
  flight_name?: string;
};

function readPushData(notification: { data?: PushData }): PushData | undefined {
  return notification.data;
}

async function handleReplayReadyPush(data: PushData): Promise<void> {
  const remoteId = data.flight_id;
  if (!remoteId) return;
  const flightName = data.flight_name ?? 'Flight';
  setGenerating(remoteId, false);
  const flightRes = await fetchRemoteFlight(remoteId);
  const replayUrl = flightRes.success ? flightRes.data?.replayUrl : null;
  if (replayUrl) {
    emitReplayReady({ remoteId, replayUrl, flightName });
  }
  await showReplayReadyAlert(flightName);
}

export function usePushNotifications(): void {
  const router = useRouter();
  const { flights, refreshFromServer } = useFlightStorage();

  function openFlightDeckForRemoteId(remoteId: string): void {
    const local = flights.value.find((f) => f.remoteId === remoteId);
    if (local) {
      router.push({ name: 'flight-deck', params: { flightId: local.id } });
      return;
    }
    void refreshFromServer().then(() => {
      const resolved = flights.value.find((f) => f.remoteId === remoteId);
      if (resolved) {
        router.push({ name: 'flight-deck', params: { flightId: resolved.id } });
      } else {
        router.push({ name: 'flights' });
      }
    });
  }

  onMounted(async () => {
    if (!Capacitor.isNativePlatform() || !isAuthenticated()) return;

    try {
      const perm = await PushNotifications.requestPermissions();
      if (perm.receive !== 'granted') return;

      await PushNotifications.register();

      PushNotifications.addListener(
        'registration',
        async (token: { value: string }) => {
          const res = await registerDeviceToken(
            token.value,
            Capacitor.getPlatform() === 'ios' ? 'ios' : 'android',
          );
          if (!res.success) {
            console.warn('Push: failed to register token', res.error);
          }
        },
      );

      PushNotifications.addListener('registrationError', (err: { error: string }) => {
        console.warn('Push: registration error', err.error);
      });

      PushNotifications.addListener('pushNotificationReceived', (notification: { data?: PushData }) => {
        const data = readPushData(notification);
        if (data?.type === 'replay_ready') {
          void handleReplayReadyPush(data);
        }
      });

      PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (action: { notification: { data?: PushData } }) => {
          const data = readPushData(action.notification);
          if (data?.type === 'replay_ready' && data.flight_id) {
            void handleReplayReadyPush(data);
            openFlightDeckForRemoteId(data.flight_id);
          }
        },
      );
    } catch (e) {
      console.warn('Push: setup failed', e);
    }
  });

  onUnmounted(() => {
    PushNotifications.removeAllListeners();
  });
}
